import React, { useEffect, useRef, useState } from 'react';
import { QrCode, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { projectAPI } from '../services/api';

/**
 * "Join my run" — companion QR + link (plan_companion_join.md).
 *
 * Shown to any member of a live run (participants modal). Anyone who scans or
 * opens the link lands in THIS run as a participant: guests are minted
 * automatically (no account needed), registered users join directly.
 * The venue can turn this off (allowCompanionJoin) — then a short notice
 * renders instead of the QR.
 */
const CompanionJoinPanel = ({ projectId, projectName }) => {
  const [joinInfo, setJoinInfo] = useState(null); // { code, path }
  const [joinQrUrl, setJoinQrUrl] = useState(null);
  const [joinDisabled, setJoinDisabled] = useState(false);
  const [joinError, setJoinError] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [qrEnlarged, setQrEnlarged] = useState(false);
  const objectUrlRef = useRef(null);

  const joinLink = joinInfo ? `${window.location.origin}${joinInfo.path}` : null;

  // One effect, keyed on projectId ONLY: code + QR are fetched in a single
  // async sequence. (Putting joinInfo in the deps self-cancels the in-flight
  // QR fetch when setJoinInfo re-triggers the effect — the "spinner forever"
  // bug from the first field test.)
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await projectAPI.createJoinCode(projectId);
        const info = res.data?.data;
        if (cancelled || !info?.code) return;
        setJoinInfo(info); // link + copy become usable before the QR arrives
        const qr = await projectAPI.getJoinCodeQr(
          projectId,
          info.code,
          window.location.origin
        );
        if (cancelled) return;
        objectUrlRef.current = URL.createObjectURL(qr.data);
        setJoinQrUrl(objectUrlRef.current);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.data?.detail === 'companion_join_disabled') {
          setJoinDisabled(true);
        } else {
          setJoinError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Release the blob URL when the panel unmounts (modal close)
  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    []
  );

  const handleCopyJoinLink = async () => {
    if (!joinLink) return;
    const isMobile = window.matchMedia('(pointer: coarse)').matches;
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: projectName || 'Okio',
          text: `Join my run: ${projectName || 'Okio'}`,
          url: joinLink,
        });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(joinLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  if (joinDisabled) {
    return (
      <div className="p-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-500 dark:text-gray-400">
        The venue has turned off group joining for this experience.
      </div>
    );
  }

  return (
    <div className="p-5 bg-emerald-50/60 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/60 rounded-xl">
      <div className="flex items-center gap-2 mb-1.5">
        <QrCode className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <span className="text-base font-semibold text-gray-900 dark:text-white">
          Invite companions
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
        Exploring together? Let them scan this — they're in.
        <br />
        <span className="text-gray-400 dark:text-gray-500">
          No app, no account needed.
        </span>
      </p>
      <div className="flex items-center gap-5">
        <div className="flex flex-col items-center flex-shrink-0">
          <button
            type="button"
            onClick={() => joinQrUrl && setQrEnlarged(true)}
            className="w-32 h-32 rounded-xl bg-white p-2 border border-gray-200 dark:border-gray-600 flex items-center justify-center cursor-pointer"
            title="Tap to enlarge"
          >
            {joinQrUrl ? (
              <img
                src={joinQrUrl}
                alt="Companion join QR — tap to enlarge"
                className="w-full h-full object-contain"
              />
            ) : joinError ? (
              <span className="text-xs text-gray-400 text-center px-2">
                QR unavailable
              </span>
            ) : (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" />
            )}
          </button>
          {joinQrUrl && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
              Tap to enlarge
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={handleCopyJoinLink}
            disabled={!joinLink}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span>{linkCopied ? 'Copied' : 'Copy link'}</span>
          </button>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2.5">
            Link works for 24 hours
          </p>
        </div>
      </div>

      {/* Fullscreen QR for actual scanning — a phone camera wants a big,
          bright target, not a 128px thumbnail */}
      {qrEnlarged && joinQrUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex flex-col items-center justify-center p-6 cursor-pointer"
          onClick={() => setQrEnlarged(false)}
          role="dialog"
          aria-label="Companion join QR, enlarged"
        >
          <div className="bg-white rounded-3xl p-6 shadow-2xl">
            <img
              src={joinQrUrl}
              alt="Companion join QR"
              className="w-[min(78vw,400px)] h-[min(78vw,400px)] object-contain"
              style={{ imageRendering: 'pixelated' }}
            />
          </div>
          {projectName && (
            <p className="text-white/90 text-base font-semibold mt-5 text-center max-w-xs">
              {projectName}
            </p>
          )}
          <p className="text-white/50 text-sm mt-1">Tap anywhere to close</p>
        </div>
      )}
    </div>
  );
};

export default CompanionJoinPanel;
